import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Modal, RadioChangeEvent } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import {
  CancelReservationCancelVisits,
  CancelReservationDelegationWarning,
  CancelReservationDetailsSection,
  CancelReservationModalFooter,
  CancelReservationModalTitle,
  CancelReservationOptionsSection,
} from '../common';
import { CancelOption, CancelReservationModalProps } from '../types';
import { SHELL_OVERLAY_Z_INDEX } from 'constants/zIndex';

export const CancelReservationSingleModal = ({
  open,
  afterOpenChange,
  onClose,
  onConfirm,
  reservationInProgress,
  reservationUserId,
  reservationUserName,
  reservationReserverName,
  showDelegatedWarning,
  reservationStartDate,
  reservationTimezone,
  deskName,
  buildingName,
  floorName,
  showMultipleOption,
  reservationDates,
  loading = false,
}: CancelReservationModalProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'bookings.cancelReservationModal',
  });
  const [cancelOption, setCancelOption] = useState<CancelOption>('single');
  const [shouldCancelVisits, setShouldCancelVisits] = useState(false);

  const reservationDate = useMemo(
    () => reservationStartDate.clone().tz(reservationTimezone),
    [reservationStartDate, reservationTimezone]
  );

  const dates = useMemo(
    () =>
      (reservationDates?.length ? reservationDates : [reservationDate])
        .map((date) => date.clone().tz(reservationTimezone))
        .sort((left, right) => left.diff(right)),
    [reservationDate, reservationDates, reservationTimezone]
  );

  const reservationDateList = useMemo(() => {
    if (dates.length === 0) {
      return '';
    }

    const isContinuous = dates.every(
      (date, index) =>
        index === dates.length - 1 || dates[index + 1].diff(date, 'days') === 1
    );

    return isContinuous
      ? `${dates[0].format('M/D')} - ${dates[dates.length - 1].format('M/D')}`
      : [...new Set(dates.map((date) => date.format('M/D')))].join(', ');
  }, [dates]);

  const handleRadioButton = useCallback(
    (event: RadioChangeEvent) => {
      const value = event.target.value as CancelOption;
      if (reservationInProgress && value === 'single') {
        setShouldCancelVisits(false);
      }
      setCancelOption(value);
    },
    [reservationInProgress]
  );

  const handleCheckboxClick = () => {
    if (reservationInProgress && cancelOption === 'single') {
      setShouldCancelVisits(false);
      return;
    }

    setShouldCancelVisits((currentValue) => !currentValue);
  };

  const handleConfirm = () => {
    onConfirm(cancelOption, shouldCancelVisits);
  };

  return (
    <Modal
      title={
        <CancelReservationModalTitle
          reservationInProgress={reservationInProgress}
        />
      }
      open={open}
      zIndex={SHELL_OVERLAY_Z_INDEX}
      afterOpenChange={afterOpenChange}
      onOk={handleConfirm}
      onCancel={onClose}
      modalRender={(modalNode) => (
        <ModalEventBoundary
          onClick={stopModalEvent}
          onMouseDown={stopModalEvent}
          onPointerDown={stopModalEvent}
        >
          {modalNode}
        </ModalEventBoundary>
      )}
      footer={
        <CancelReservationModalFooter
          onClose={onClose}
          handleConfirm={handleConfirm}
          loading={loading}
        />
      }
    >
      <ModalBody>
        <div>{t('confirm_cancel')}</div>

        <CancelReservationDelegationWarning
          showDelegatedWarning={!!showDelegatedWarning}
          reservationReserveeName={reservationUserName}
          reservationReserverName={reservationReserverName}
        />

        <CancelReservationDetailsSection
          reservationUserName={reservationUserName}
          deskName={deskName}
          floorName={floorName}
          buildingName={buildingName}
        >
          {cancelOption === 'single'
            ? reservationDate.format('dddd, MMMM DD')
            : loading
            ? t('loading')
            : reservationDateList}
        </CancelReservationDetailsSection>

        {showMultipleOption && (
          <CancelReservationOptionsSection
            selectedOption={cancelOption}
            isSeries={false}
            reservationStartDate={reservationDate}
            reservationInProgress={reservationInProgress}
            loading={loading}
            handleRadioButton={handleRadioButton}
          />
        )}

        <CancelReservationCancelVisits
          selectedOption={cancelOption}
          reservationInProgress={reservationInProgress}
          shouldCancelVisits={shouldCancelVisits}
          handleCheckboxClick={handleCheckboxClick}
          reservationUserId={reservationUserId}
          showDelegatedWarning={showDelegatedWarning}
        />
      </ModalBody>
    </Modal>
  );
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalEventBoundary = styled.div``;

const stopModalEvent = (event: SyntheticEvent<HTMLDivElement>) => {
  event.stopPropagation();
  event.nativeEvent.stopImmediatePropagation?.();
};
